import { Attachment, TemplateData } from '../../types/blueprint'

import { DOCUMENT_FORMATS, PDF } from '@/config/constants/fileTypes'
import { extensionFromUrl } from '@/modules/attachments/attachmentHelpers'

type TemplateAttachment =
  | {
      url: string
      id: string
      blockId: string
      blockName: string
      thumbnailUrl?: string
    }
  | Attachment

// Why do we do this instead of filtering like we do for PDFs?
// Because older files don't have the extension in the URL, we can't rely on it for old templates
export const getTemplateImages = (
  templateData: TemplateData,
  includeImageBlocks = true
): TemplateAttachment[] => {
  return (
    (
      templateData?.sections?.reduce((agg, curr) => {
        for (const blockGroup of curr.blockGroups) {
          for (const block of blockGroup.blocks) {
            if (includeImageBlocks) {
              // Some customers relied on not having these files in the appendix
              // in the future we could make it optional as they export the PDF and have a setting
              if (block.type === 'image') {
                agg.push({
                  url: block.url,
                  id: block.id,
                  blockId: block.id,
                  blockName: block.name,
                })
              }
              if (block.type === 'file') {
                block.fileList?.forEach((file) => {
                  agg.push({
                    url: file.url,
                    id: file.attachmentId,
                    blockId: block.id,
                    blockName: block.name,
                  })
                })
              }
            }

            if (block.attachments) {
              agg.push(
                ...(block?.attachments?.map?.(
                  (attachment): TemplateAttachment => ({
                    url: attachment.url,
                    id: attachment.id,
                    blockId: block.id,
                    blockName: block.name,
                  })
                ) || [])
              )
            }

            if (block.platformActions) {
              block.platformActions?.forEach?.((platformAction) => {
                platformAction.attachments?.forEach((attachment) => {
                  agg.push({
                    url: attachment.url,
                    id: attachment.id,
                    blockId: block.id,
                    blockName: block.name,
                  })
                })
              })
            }
          }
        }
        return agg
      }, [] as TemplateAttachment[]) || []
    )
      // Naively filter out non image types
      ?.filter(
        (maybeImage) =>
          !DOCUMENT_FORMATS.includes(extensionFromUrl(maybeImage.url))
      )
  )
}

const getTemplateAttachmentsForType = (
  templateData: TemplateData,
  includeImageBlocks = true,
  type: string[] // e.g. IMAGE_FORMATS, PDF
) => {
  // this fetches all attachments then filters them by type
  return (
    templateData?.sections?.reduce((agg, curr) => {
      for (const blockGroup of curr.blockGroups) {
        for (const block of blockGroup.blocks) {
          if (includeImageBlocks) {
            // Some customers relied on not having these files in the appendix
            // in the future we should make it optional as they export the PDF and have a setting
            // hugh is turning it back on by default
            if (block.type === 'image') {
              agg.push({
                url: block.url,
                id: block.id,
                blockId: block.id,
                blockName: block.name,
              })
            }
          }

          if (block.type === 'file') {
            block.fileList?.forEach((file) => {
              agg.push({
                url: file.url,
                id: file.attachmentId,
                name: file.name,
                blockId: block.id,
                blockName: block.name,
              })
            })
          }

          if (block.attachments) {
            agg.push(
              ...(block?.attachments?.map?.(
                (attachment): TemplateAttachment => ({
                  url: attachment.url,
                  id: attachment.id,
                  blockId: block.id,
                  blockName: block.name,
                })
              ) || [])
            )
          }

          if (block.platformActions) {
            block.platformActions?.forEach?.((platformAction) => {
              platformAction.attachments?.forEach((attachment) => {
                agg.push({
                  url: attachment.url,
                  id: attachment.id,
                  blockId: block.id,
                  blockName: block.name,
                })
              })
            })
          }
        }
      }
      return agg
    }, [] as TemplateAttachment[]) ?? []
  ).filter((att) => att?.url && type.includes(extensionFromUrl(att.url)))
}

export const getTemplatePdfAttachments = (
  templateData: TemplateData
): Attachment[] => {
  return getTemplateAttachmentsForType(templateData, true, [PDF])
}
